import {
  Account,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from '@solana/web3.js';
import {
  ACCOUNT_LAYOUT,
  getOwnedAccountsFilters,
  MINT_LAYOUT,
  parseTokenAccountData,
} from './data';
import {
  closeAccount,
  initializeAccount,
  initializeMint,
  memoInstruction,
  mintTo,
  syncNative,
  TOKEN_PROGRAM_ID,
  transferChecked,
  WRAPPED_DOMI_MINT,
} from './instructions';
import {
  createCreateMetadataAccountV3Instruction,
  getMetadataPDA,
  TOKEN_METADATA_PROGRAM_ID,
} from './metadata';

export async function getOwnedTokenAccounts(connection, publicKey) {
  let filters = getOwnedAccountsFilters(publicKey);
  let resp = await connection.getProgramAccounts(TOKEN_PROGRAM_ID, {
    filters,
  });
  return resp.map(
    ({ pubkey, account: { data, executable, owner, lamports } }) => ({
      publicKey: new PublicKey(pubkey),
      accountInfo: {
        data,
        executable,
        owner: new PublicKey(owner),
        lamports,
      },
    }),
  );
}

/**
 * @returns {Promise<string>}
 */
export async function signAndSendTransaction(
  connection,
  transaction,
  wallet,
  signers,
  skipPreflight = false,
) {
  transaction.recentBlockhash = (
    await connection.getRecentBlockhash('max')
  ).blockhash;
  transaction.setSigners(
    // fee payed by the wallet owner
    wallet.publicKey,
    ...signers.map((s) => s.publicKey),
  );

  if (signers.length > 0) {
    transaction.partialSign(...signers);
  }

  transaction = await wallet.signTransaction(transaction);
  const rawTransaction = transaction.serialize();
  return await connection.sendRawTransaction(rawTransaction, {
    skipPreflight,
    preflightCommitment: 'single',
  });
}

export async function nativeTransfer(connection, wallet, destination, amount) {
  const tx = new Transaction().add(
    SystemProgram.transfer({
      fromPubkey: wallet.publicKey,
      toPubkey: destination,
      lamports: amount,
    }),
  );
  return await signAndSendTransaction(connection, tx, wallet, []);
}

export async function createAndInitializeMint({
  connection,
  owner, // Wallet for paying fees and allowed to mint new tokens
  mint, // Account to hold token information
  name,
  symbol,
  amount, // Number of tokens to issue
  decimals,
  initialAccount, // Account to hold newly issued tokens, if amount > 0
  uri = ''
}) {
  let transaction = new Transaction();
  transaction.add(
    SystemProgram.createAccount({
      fromPubkey: owner.publicKey,
      newAccountPubkey: mint.publicKey,
      lamports: await connection.getMinimumBalanceForRentExemption(
        MINT_LAYOUT.span,
      ),
      space: MINT_LAYOUT.span,
      programId: TOKEN_PROGRAM_ID,
    }),
  );
  transaction.add(
    initializeMint({
      mint: mint.publicKey,
      decimals,
      mintAuthority: owner.publicKey,
    }),
  );
  let signers = [mint];
  if (amount > 0) {
    transaction.add(
      SystemProgram.createAccount({
        fromPubkey: owner.publicKey,
        newAccountPubkey: initialAccount.publicKey,
        lamports: await connection.getMinimumBalanceForRentExemption(
          ACCOUNT_LAYOUT.span,
        ),
        space: ACCOUNT_LAYOUT.span,
        programId: TOKEN_PROGRAM_ID,
      }),
    );
    signers.push(initialAccount);
    transaction.add(
      initializeAccount({
        account: initialAccount.publicKey,
        mint: mint.publicKey,
        owner: owner.publicKey,
      }),
    );
    transaction.add(
      mintTo({
        mint: mint.publicKey,
        destination: initialAccount.publicKey,
        amount,
        mintAuthority: owner.publicKey,
      }),
    );
  }
  transaction.add(
    createCreateMetadataAccountV3Instruction(
      {
        metadata: getMetadataPDA(mint.publicKey),
        mint: mint.publicKey,
        mintAuthority: owner.publicKey,
        payer: owner.publicKey,
        updateAuthority: owner.publicKey,
      },
      {
        createMetadataAccountArgsV3: {
          data: {
            name,
            symbol,
            uri: uri, // Arweave URI link which uses metaplex standard if you're making art NFTs
            sellerFeeBasisPoints: 0,
            creators: [
              {
                address: owner.publicKey,
                verified: false,
                share: 100,
              },
            ],
            collection: null,
            uses: null,
          },
          isMutable: true,
          collectionDetails: null,
        },
      },
      TOKEN_METADATA_PROGRAM_ID,
    ),
  );
  return await signAndSendTransaction(connection, transaction, owner, signers);
}

export async function createAndInitializeTokenAccount({
  connection,
  payer,
  mintPublicKey,
  newAccount,
}) {
  let transaction = new Transaction();
  transaction.add(
    SystemProgram.createAccount({
      fromPubkey: payer.publicKey,
      newAccountPubkey: newAccount.publicKey,
      lamports: await connection.getMinimumBalanceForRentExemption(
        ACCOUNT_LAYOUT.span,
      ),
      space: ACCOUNT_LAYOUT.span,
      programId: TOKEN_PROGRAM_ID,
    }),
  );
  transaction.add(
    initializeAccount({
      account: newAccount.publicKey,
      mint: mintPublicKey,
      owner: payer.publicKey,
    }),
  );

  let signers = [newAccount];
  return await signAndSendTransaction(connection, transaction, payer, signers);
}

export async function createAssociatedTokenAccount({
  connection,
  wallet,
  dplTokenMintAddress,
}) {
  const [ix, address] = await createAssociatedTokenAccountIx(
    wallet.publicKey,
    wallet.publicKey,
    dplTokenMintAddress,
  );
  const tx = new Transaction();
  tx.add(ix);
  tx.feePayer = wallet.publicKey;
  const txSig = await signAndSendTransaction(connection, tx, wallet, []);

  return [address, txSig];
}
async function createAssociatedTokenAccountIx(
  fundingAddress,
  walletAddress,
  dplTokenMintAddress,
  programId,
) {
  const associatedTokenAddress = findAssociatedTokenAddress(
    walletAddress,
    dplTokenMintAddress,
    programId,
  );
  const keys = [
    {
      pubkey: fundingAddress,
      isSigner: true,
      isWritable: true,
    },
    {
      pubkey: associatedTokenAddress,
      isSigner: false,
      isWritable: true,
    },
    {
      pubkey: walletAddress,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: dplTokenMintAddress,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: SystemProgram.programId,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: programId,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: SYSVAR_RENT_PUBKEY,
      isSigner: false,
      isWritable: false,
    },
  ];
  const ix = new TransactionInstruction({
    keys,
    programId: ASSOCIATED_TOKEN_PROGRAM_ID,
    data: Buffer.from([]),
  });
  return [ix, associatedTokenAddress];
}

export function findAssociatedTokenAddress(
  walletAddress,
  tokenMintAddress,
  programId = TOKEN_PROGRAM_ID,
) {
  return PublicKey.findProgramAddressSync(
    [
      walletAddress.toBuffer(),
      programId.toBuffer(),
      tokenMintAddress.toBuffer(),
    ],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )[0];
}

export const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey(
  'Dt8fRCpjeV6JDemhPmtcTKijgKdPxXHn9Wo9cXY5agtG',
);

export async function transferTokens({
  connection,
  owner,
  sourcePublicKey,
  destinationPublicKey,
  amount,
  memo,
  mint,
  decimals,
  overrideDestinationCheck,
  programId = TOKEN_PROGRAM_ID,
}) {
  let destinationAccountInfo = await connection.getAccountInfo(
    destinationPublicKey,
  );
  if (
    !!destinationAccountInfo &&
    destinationAccountInfo.owner.equals(programId)
  ) {
    return await transferBetweenSplTokenAccounts({
      connection,
      owner,
      mint,
      decimals,
      sourcePublicKey,
      destinationPublicKey,
      amount,
      memo,
      programId,
    });
  }

  if (
    (!destinationAccountInfo || destinationAccountInfo.lamports === 0) &&
    !overrideDestinationCheck
  ) {
    throw new Error('Cannot send to address with zero DOMI balances');
  }

  const destinationAssociatedTokenAddress = findAssociatedTokenAddress(
    destinationPublicKey,
    mint,
    programId,
  );
  destinationAccountInfo = await connection.getAccountInfo(
    destinationAssociatedTokenAddress,
  );
  if (
    !!destinationAccountInfo &&
    destinationAccountInfo.owner.equals(programId)
  ) {
    return await transferBetweenSplTokenAccounts({
      connection,
      owner,
      mint,
      decimals,
      sourcePublicKey,
      destinationPublicKey: destinationAssociatedTokenAddress,
      amount,
      memo,
      programId,
    });
  }
  return await createAndTransferToAccount({
    connection,
    owner,
    sourcePublicKey,
    destinationPublicKey,
    amount,
    memo,
    mint,
    decimals,
    programId,
  });
}

function createTransferBetweenSplTokenAccountsInstruction({
  ownerPublicKey,
  mint,
  decimals,
  sourcePublicKey,
  destinationPublicKey,
  amount,
  memo,
  programId,
}) {
  let transaction = new Transaction().add(
    transferChecked({
      source: sourcePublicKey,
      mint,
      decimals,
      destination: destinationPublicKey,
      owner: ownerPublicKey,
      amount,
      programId,
    }),
  );
  if (memo) {
    transaction.add(memoInstruction(memo));
  }
  return transaction;
}

async function transferBetweenSplTokenAccounts({
  connection,
  owner,
  mint,
  decimals,
  sourcePublicKey,
  destinationPublicKey,
  amount,
  memo,
  programId,
}) {
  const transaction = createTransferBetweenSplTokenAccountsInstruction({
    ownerPublicKey: owner.publicKey,
    mint,
    decimals,
    sourcePublicKey,
    destinationPublicKey,
    amount,
    memo,
    programId,
  });
  return transaction;
}

async function createAndTransferToAccount({
  connection,
  owner,
  sourcePublicKey,
  destinationPublicKey,
  amount,
  memo,
  mint,
  decimals,
  programId,
}) {
  const [createAccountInstruction, newAddress] =
    await createAssociatedTokenAccountIx(
      owner.publicKey,
      destinationPublicKey,
      mint,
      programId,
    );
  const transaction = new Transaction().add(
    createAccountInstruction,
    createTransferBetweenSplTokenAccountsInstruction({
      ownerPublicKey: owner.publicKey,
      mint,
      decimals,
      sourcePublicKey,
      destinationPublicKey: newAddress,
      amount,
      memo,
      programId,
    }),
  );
  // transaction.add(
  //   assertOwner({
  //     account: destinationPublicKey,
  //     owner: SystemProgram.programId,
  //   }),
  // );
  return transaction;
}

// Wrap native DOMI into wDOMI (the native mint, So111...112).
//
// Everything lands in the associated token account — the address dapps
// derive from the wallet key. If an older auxiliary wDOMI account exists,
// its balance is folded into the ATA and the account is closed, so the
// rent comes back too.
export async function wrapDomi({ connection, wallet, amount, existingAccount }) {
  const transaction = new Transaction();
  const ata = findAssociatedTokenAddress(wallet.publicKey, WRAPPED_DOMI_MINT);

  const ataInfo = await connection.getAccountInfo(ata);
  if (!ataInfo) {
    const [createIx] = await createAssociatedTokenAccountIx(
      wallet.publicKey,
      wallet.publicKey,
      WRAPPED_DOMI_MINT,
      TOKEN_PROGRAM_ID,
    );
    transaction.add(createIx);
  }

  if (existingAccount && !existingAccount.equals(ata)) {
    const oldInfo = await connection.getAccountInfo(existingAccount);
    if (oldInfo) {
      const { amount: oldAmount } = parseTokenAccountData(oldInfo.data);
      if (oldAmount > 0) {
        transaction.add(
          transferChecked({
            source: existingAccount,
            mint: WRAPPED_DOMI_MINT,
            destination: ata,
            owner: wallet.publicKey,
            amount: oldAmount,
            decimals: 9,
            programId: TOKEN_PROGRAM_ID,
          }),
        );
      }
      transaction.add(
        closeAccount({
          source: existingAccount,
          destination: wallet.publicKey,
          owner: wallet.publicKey,
        }),
      );
    }
  }

  transaction.add(
    SystemProgram.transfer({
      fromPubkey: wallet.publicKey,
      toPubkey: ata,
      lamports: amount,
    }),
    syncNative({ account: ata }),
  );

  return await signAndSendTransaction(connection, transaction, wallet, []);
}

// Unwrap wDOMI back to native DOMI.
//
// Unwrapping everything closes the account, which returns the satomis plus
// the rent deposit. A partial unwrap hops the amount through a throwaway
// native account that gets closed in the same transaction, so the rent for
// the hop comes straight back.
export async function unwrapDomi({
  connection,
  wallet,
  sourcePublicKey,
  amount,
  totalAmount,
}) {
  const transaction = new Transaction();
  const signers = [];

  if (amount >= totalAmount) {
    transaction.add(
      closeAccount({
        source: sourcePublicKey,
        destination: wallet.publicKey,
        owner: wallet.publicKey,
      }),
    );
  } else {
    const tempAccount = new Account();
    const rent = await connection.getMinimumBalanceForRentExemption(
      ACCOUNT_LAYOUT.span,
    );
    transaction.add(
      SystemProgram.createAccount({
        fromPubkey: wallet.publicKey,
        newAccountPubkey: tempAccount.publicKey,
        lamports: rent,
        space: ACCOUNT_LAYOUT.span,
        programId: TOKEN_PROGRAM_ID,
      }),
      initializeAccount({
        account: tempAccount.publicKey,
        mint: WRAPPED_DOMI_MINT,
        owner: wallet.publicKey,
      }),
      transferChecked({
        source: sourcePublicKey,
        mint: WRAPPED_DOMI_MINT,
        destination: tempAccount.publicKey,
        owner: wallet.publicKey,
        amount,
        decimals: 9,
        programId: TOKEN_PROGRAM_ID,
      }),
      closeAccount({
        source: tempAccount.publicKey,
        destination: wallet.publicKey,
        owner: wallet.publicKey,
      }),
    );
    signers.push(tempAccount);
  }

  return await signAndSendTransaction(connection, transaction, wallet, signers);
}

export async function closeTokenAccount({
  connection,
  owner,
  sourcePublicKey,
  skipPreflight,
}) {
  let transaction = new Transaction().add(
    closeAccount({
      source: sourcePublicKey,
      destination: owner.publicKey,
      owner: owner.publicKey,
    }),
  );
  let signers = [];
  return await signAndSendTransaction(
    connection,
    transaction,
    owner,
    signers,
    skipPreflight,
  );
}
